/**
 * Clip box text to fit within box dimensions using canvas measureText.
 * Uses system-installed fonts via fontFamily only (no fontPath/registerFont).
 *
 * Uses document.createElement('canvas') for browser compatibility.
 * For Node.js: replace getMeasureContext with createCanvas from 'canvas' package.
 */

export interface ClipBoxTextResult {
  visibleText: string;
  overflow: boolean;
}

export interface ClipBoxTextParams {
  /** Pre-scaled font size (style.fontSize * scaleY) - used directly for measurement */
  text: string;
  fontSizePx: number;
  /** Pre-scaled line spacing (leading * scaleY) - used directly */
  lineSpacingPx: number;
  boxWidthPx: number;
  boxHeightPx: number;
  fontFamily: string;
  scaleX: number;
  scaleY: number;
}

function getMeasureContext(): CanvasRenderingContext2D {
  if (typeof document !== 'undefined') {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) return ctx;
  }
  throw new Error('Canvas 2d context not available for text measurement');
}

/**
 * Clips text to fit within a box using word wrapping and line limit.
 * Returns visible text and overflow flag.
 */
export function clipBoxText(params: ClipBoxTextParams): ClipBoxTextResult {
  const {
    text,
    fontSizePx,
    lineSpacingPx,
    boxWidthPx,
    boxHeightPx,
    fontFamily,
    scaleX,
    scaleY
  } = params;

  // Preprocess: normalize line endings only, preserve newlines
  const normalizedText = text.replaceAll('\r\n', '\n');

  // fontSizePx and lineSpacingPx are pre-scaled by caller (style.fontSize * scaleY, leading * scaleY)
  const effectiveFontSize = fontSizePx;
  const effectiveLeading = lineSpacingPx;
  const effectiveWidth = boxWidthPx * scaleX;
  const effectiveHeight = boxHeightPx * scaleY;
  const maxLines = effectiveLeading > 0
    ? Math.floor(effectiveHeight / effectiveLeading)
    : 1;

  if (maxLines <= 0 || effectiveWidth <= 0) {
    return { visibleText: '', overflow: normalizedText.length > 0 };
  }

  const ctx = getMeasureContext();
  ctx.font = `${effectiveFontSize}px ${fontFamily}`;

  function wrapWordToChars(word: string): string[] {
    const out: string[] = [];
    let charLine = '';
    for (const char of word) {
      const charTest = charLine + char;
      if (ctx.measureText(charTest).width <= effectiveWidth) {
        charLine = charTest;
      } else {
        if (charLine) out.push(charLine);
        charLine = char;
      }
    }
    if (charLine) out.push(charLine);
    return out;
  }

  function wrapParagraph(paragraph: string): string[] {
    const words = paragraph.split(/\s+/).filter(Boolean);
    const out: string[] = [];
    let currentLine = '';
    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (ctx.measureText(testLine).width <= effectiveWidth) {
        currentLine = testLine;
      } else {
        if (currentLine) out.push(currentLine);
        const wordLines = ctx.measureText(word).width <= effectiveWidth
          ? [word]
          : wrapWordToChars(word);
        const lastIdx = wordLines.length - 1;
        wordLines.slice(0, lastIdx).forEach((line) => out.push(line));
        currentLine = wordLines[lastIdx] ?? '';
      }
    }
    if (currentLine) out.push(currentLine);
    return out;
  }

  const paragraphs = normalizedText.split('\n');
  const lines: string[] = [];
  let overflow = false;

  for (const paragraph of paragraphs) {
    if (lines.length >= maxLines) {
      overflow = true;
      break;
    }
    const trimmed = paragraph.trim();
    const wrapped = trimmed ? wrapParagraph(trimmed) : [''];
    for (const line of wrapped) {
      if (lines.length >= maxLines) {
        overflow = true;
        break;
      }
      lines.push(line);
    }
  }

  const visibleText = lines.join(' ');
  return {
    visibleText,
    overflow
  };
}
